/**
 * ExtensionPrompt — contextual, dismissible banner encouraging Chrome extension install.
 * Only shown on desktop (extension can't be installed on mobile).
 * Remembers dismissal in localStorage per variant.
 */

import { useState } from 'react';

const CWS_URL = 'https://chromewebstore.google.com/detail/ats-cv-checker/acfhjcapfkflfjdgcmhfnjhmjhagllgn?hl=en&utm_source=webapp&utm_medium=banner';

interface ExtensionPromptProps {
  variant: 'pipeline-empty' | 'no-description' | 'post-onboarding' | 'post-cv' | 'analytics';
}

const MESSAGES: Record<ExtensionPromptProps['variant'], { icon: string; title: string; text: string; cta: string }> = {
  'pipeline-empty': {
    icon: '🔍',
    title: 'Auto-save jobs while you browse',
    text: 'Install the Chrome extension to automatically detect and save vacancies from LinkedIn, Indeed, and 30+ job boards.',
    cta: 'Get Chrome Extension',
  },
  'no-description': {
    icon: '📋',
    title: 'Auto-capture job descriptions',
    text: 'The Chrome extension automatically captures full job descriptions, enabling ATS matching and tailored CV generation.',
    cta: 'Install Extension',
  },
  'post-onboarding': {
    icon: '🚀',
    title: 'Get real-time ATS scores on job pages',
    text: 'Install the Chrome extension to see your ATS match score directly on LinkedIn, Indeed, and other job boards while you browse.',
    cta: 'Add to Chrome — Free',
  },
  'post-cv': {
    icon: '⚡',
    title: 'Apply faster with auto-fill',
    text: 'The Chrome extension auto-fills job applications on Greenhouse, Workday, LinkedIn Easy Apply, and more.',
    cta: 'Get Auto-Fill Extension',
  },
  'analytics': {
    icon: '📊',
    title: 'Unlock real-time ATS scoring',
    text: 'See your match score instantly on any job page. The Chrome extension analyzes your profile against each vacancy in real time.',
    cta: 'Install Extension',
  },
};

function isMobile(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768;
}

export function ExtensionPrompt({ variant }: ExtensionPromptProps) {
  const storageKey = `extension-prompt-dismissed-${variant}`;
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === 'true';
    } catch {
      return false;
    }
  });

  if (dismissed || isMobile()) return null;

  const msg = MESSAGES[variant];

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(storageKey, 'true');
    } catch {
      // localStorage unavailable
    }
  };

  return (
    <div className="bg-surface border border-border rounded-lg p-4 flex items-start gap-3">
      <span className="text-xl flex-shrink-0">{msg.icon}</span>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-text-primary text-sm">{msg.title}</p>
        <p className="text-text-secondary text-xs mt-0.5">{msg.text}</p>
        <a
          href={CWS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-md text-xs font-medium bg-primary text-white hover:bg-primary-dark transition-colors"
        >
          {msg.cta}
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      </div>
      <button
        onClick={handleDismiss}
        className="text-text-secondary hover:text-text-primary text-sm flex-shrink-0 p-1"
        aria-label="Dismiss"
      >
        ✕
      </button>
    </div>
  );
}
